import { useLayoutEffect, useRef, useState } from "react";

const NODES = {
  farmer: { x: 70, y: 160, label: "Farmer", lx: 0, ly: 38 },
  trader: { x: 205, y: 115, label: "Trader", lx: 0, ly: -24 },
  wholesale: { x: 345, y: 175, label: "Wholesaler", lx: 0, ly: 38 },
  retail: { x: 480, y: 120, label: "Shop", lx: 0, ly: -24 },
  kitchen: { x: 615, y: 175, label: "Kitchen", lx: 34, ly: 34 },
  family: { x: 750, y: 130, label: "Family", lx: 0, ly: -24 },
  seeds: { x: 120, y: 45, label: "Seed bank", lx: 0, ly: -18, extra: true },
  coop: { x: 215, y: 270, label: "Cooperative", lx: 0, ly: 36, extra: true },
  market: { x: 450, y: 275, label: "Local market", lx: 0, ly: 36, extra: true },
  school: { x: 400, y: 40, label: "School", lx: 0, ly: -18, extra: true },
  homestay: { x: 700, y: 270, label: "Homestay", lx: 0, ly: 36, extra: true },
};
const CHAIN = [["farmer", "trader"], ["trader", "wholesale"], ["wholesale", "retail"], ["retail", "kitchen"], ["kitchen", "family"]];
const EXTRA = [
  ["seeds", "farmer"], ["farmer", "coop"], ["coop", "market"], ["coop", "wholesale"],
  ["market", "kitchen"], ["market", "family"], ["trader", "school"], ["school", "retail"],
  ["school", "family"], ["kitchen", "homestay"], ["homestay", "family"], ["seeds", "school"],
];
const EDGES = [...CHAIN.map(([a, b]) => ({ a, b, extra: false })), ...EXTRA.map(([a, b]) => ({ a, b, extra: true }))];
const BREAK = ["trader", "wholesale"];
const isBreakEdge = (e) => (e.a === BREAK[0] && e.b === BREAK[1]) || (e.a === BREAK[1] && e.b === BREAK[0]);

function reachable(mode, broken) {
  const active = EDGES.filter((e) => (mode === "network" || !e.extra) && !(broken && isBreakEdge(e)));
  const seen = new Set(["farmer"]);
  const queue = ["farmer"];
  while (queue.length) {
    const cur = queue.shift();
    active.forEach((e) => {
      const next = e.a === cur ? e.b : e.b === cur ? e.a : null;
      if (next && !seen.has(next)) { seen.add(next); queue.push(next); }
    });
  }
  return seen;
}

function statusText(mode, broken, lost) {
  if (mode === "chain" && !broken) return "A chain: food passes from one hand to the next. Try breaking a link.";
  if (mode === "chain") return `One broken link, and ${lost} of 6 steps are cut off from the farmer. This is what happened in the lockdowns.`;
  if (!broken) return "A network: farmers, cooperatives, schools, markets and kitchens are linked in many directions. Try breaking a link.";
  return lost === 0 ? "The same link is broken, and everyone is still connected. Food finds another way." : `${lost} cut off.`;
}

const useVertical = (ref) => {
  const [vertical, setVertical] = useState(false);
  useLayoutEffect(() => {
    const place = () => setVertical(ref.current.parentElement.clientWidth < 560);
    place();
    let rt;
    const onResize = () => { clearTimeout(rt); rt = setTimeout(place, 150); };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); clearTimeout(rt); };
  }, [ref]);
  return vertical;
};

export const ChainNetworkDiagram = () => {
  const [mode, setMode] = useState("chain");
  const [broken, setBroken] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const svgRef = useRef(null);
  const vertical = useVertical(svgRef);

  const pos = (id) => (vertical ? { x: NODES[id].y + 30, y: NODES[id].x } : { x: NODES[id].x, y: NODES[id].y });
  const visible = (extra) => mode === "network" || !extra;
  const seen = reachable(mode, broken);
  const lost = Object.keys(NODES).filter((id) => visible(NODES[id].extra) && !seen.has(id)).length;
  const a = pos(BREAK[0]), b = pos(BREAK[1]);
  const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;

  return (
    <figure className="netbox" aria-labelledby="net-caption" data-testid="chain-network-diagram">
      <div className="net-controls">
        <div className="seg" role="group" aria-label="Show">
          <button type="button" id="btn-chain" aria-pressed={mode === "chain"} onClick={() => setMode("chain")} data-testid="diagram-chain-button">Chain</button>
          <button type="button" id="btn-net" aria-pressed={mode === "network"} onClick={() => { setMode("network"); setDrawn(true); }} data-testid="diagram-network-button">Network</button>
        </div>
        <button type="button" className="break-btn" id="btn-break" aria-pressed={broken} onClick={() => setBroken(!broken)} data-testid="diagram-break-button">
          {broken ? "Repair the link" : "Break a link"}
        </button>
      </div>
      <svg id="net" ref={svgRef} viewBox={vertical ? "0 0 470 830" : "0 0 820 320"} role="img" aria-labelledby="net-caption" data-testid="diagram-svg">
        <g>
          {EDGES.map((e) => {
            const p = pos(e.a), q = pos(e.b);
            const cls = ["edge", e.extra && "extra", !visible(e.extra) && "hidden", broken && isBreakEdge(e) && "broken", e.extra && drawn && "draw"];
            return <line key={e.a + e.b} x1={p.x} y1={p.y} x2={q.x} y2={q.y} className={cls.filter(Boolean).join(" ")} />;
          })}
          <path className={broken ? "cutmark on" : "cutmark"} d={`M${cx - 10} ${cy - 10} L${cx + 10} ${cy + 10} M${cx + 10} ${cy - 10} L${cx - 10} ${cy + 10}`} />
        </g>
        <g>
          {Object.entries(NODES).map(([id, n]) => {
            const p = pos(id);
            const show = visible(n.extra);
            const cls = ["node", n.extra && "extra", !show && "hidden", show && !seen.has(id) && "cut"];
            const t = vertical ? { x: p.x + 20, y: p.y + 6, anchor: "start" } : { x: p.x + n.lx, y: p.y + n.ly, anchor: "middle" };
            return (
              <g key={id} className={cls.filter(Boolean).join(" ")} data-testid={`diagram-node-${id}`}>
                <circle cx={p.x} cy={p.y} r={id === "farmer" ? 14 : 11} />
                <text x={t.x} y={t.y} textAnchor={t.anchor}>{n.label}</text>
              </g>
            );
          })}
        </g>
      </svg>
      <figcaption>
        <p className="net-status" id="net-caption" aria-live="polite" data-testid="diagram-status">{statusText(mode, broken, lost)}</p>
      </figcaption>
    </figure>
  );
};
